import React from 'react';
import { DashboardStats, Partner } from '../../types/api';

interface VenueAboutProps {
  partner: Partner | null;
  stats: DashboardStats | null;
  onEdit: () => void;
}

function formatMonth(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
}

/**
 * «Чем занимается» и «Описание» — то, по чему заведение находят в
 * приложении.
 *
 * Факты в нижней сетке намеренно универсальные: QPrice держит не только
 * рестораны, и «посадочных мест» у автомойки не бывает. Здесь только то,
 * что осмысленно для любого заведения и что бэк действительно отдаёт.
 */
export const VenueAbout: React.FC<VenueAboutProps> = ({ partner, stats, onEdit }) => {
  const categories = partner?.categories
    ? [...partner.categories].sort(
        (a, b) => Number(b.id === partner.primary_category?.id) - Number(a.id === partner.primary_category?.id),
      )
    : [];
  const subcategories = partner?.subcategories ?? [];

  return (
    <>
      <section className="qp-panel">
        <header className="qp-panel-head">
          <h2 className="qp-label">Чем занимается</h2>
          <button type="button" className="qp-link" onClick={onEdit}>
            Изменить
          </button>
        </header>

        {categories.length > 0 ? (
          <div className="qp-chips">
            {categories.map((category) => (
              <span
                key={category.id}
                className={`qp-chip${category.id === partner?.primary_category?.id ? ' qp-chip--accent' : ''}`}
              >
                {category.title}
              </span>
            ))}
          </div>
        ) : (
          <p className="qp-muted" style={{ margin: 0, fontSize: 13 }}>
            Категории не выбраны — без них заведение не попадёт в нужные подборки.
          </p>
        )}

        {subcategories.length > 0 && (
          <>
            <h3 className="qp-label" style={{ margin: '20px 0 12px' }}>
              Специализация
            </h3>
            <div className="qp-chips">
              {subcategories.map((subcategory) => (
                <span key={subcategory.id} className="qp-chip">
                  {subcategory.title}
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="qp-panel">
        <header className="qp-panel-head">
          <h2 className="qp-label">Описание</h2>
          <button type="button" className="qp-link" onClick={onEdit}>
            Редактировать
          </button>
        </header>

        {partner?.description ? (
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.62,
              color: 'var(--qp2-muted)',
              maxWidth: '66ch',
              whiteSpace: 'pre-wrap',
            }}
          >
            {partner.description}
          </p>
        ) : (
          <p className="qp-faint" style={{ margin: 0, fontSize: 14 }}>
            Описание не заполнено. Пара предложений о заведении — это первое, что читает клиент.
          </p>
        )}

        <div className="qp-facts">
          <div className="qp-fact">
            <span className="qp-fact-k">Рейтинг</span>
            <span className="qp-fact-v qp-mono">
              {partner?.average_rating ? partner.average_rating.toFixed(1) : '—'}
            </span>
          </div>
          <div className="qp-fact">
            <span className="qp-fact-k">Отзывов</span>
            <span className="qp-fact-v qp-mono">{stats?.reviews_count ?? '—'}</span>
          </div>
          <div className="qp-fact">
            <span className="qp-fact-k">Категорий в каталоге</span>
            <span className="qp-fact-v qp-mono">{stats?.menu_categories_count ?? '—'}</span>
          </div>
          <div className="qp-fact">
            <span className="qp-fact-k">В QPrice с</span>
            <span className="qp-fact-v">{formatMonth(partner?.created_at)}</span>
          </div>
        </div>
      </section>
    </>
  );
};
